import { useEffect, useRef, useState, useCallback, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { toJS } from 'mobx';
import { UseDataProps } from './types';
import { RadioListQcFieldName } from './const';
import { AllFieldValueTypes, Choice, ComboDesc, FieldDesc, SharedList } from '90.quickConnect.Models/models';
import { useStore } from '30.quickConnect.Stores';
import { useQCUpdateFunctionsContext } from '20.formLib/DeclarationContainer/contexts';
import { parseChoice } from '70.quickConnect.Common/fields';
import { StringExtension } from '80.quickConnect.Core/formatting/StringExtension';
import { isApplicativeListId } from '80.quickConnect.Core/const/applicativeListIds';

const useData = (
  field: FieldDesc,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  setSelectedIndex: Dispatch<SetStateAction<string | undefined>>,
): UseDataProps => {
  // On récupère le store
  const {
    SharedListStore: { getSharedList },
  } = useStore();

  const { updateProperty } = useQCUpdateFunctionsContext();
  // On récupère la translation
  const { t } = useTranslation('declaration');
  const comboDesc = field as ComboDesc;
  const { listId, listChoice, toolTip, id, fullPathId, defaultValue, value, filterDef } = comboDesc;
  const nameWithId = `${RadioListQcFieldName}-${id}`;
  const [choices, setChoices] = useState<Choice[] | undefined>(listChoice);

  const [isDeleteAll, setIsDeleteAll] = useState<boolean>(false);

  // On définit les callbacks
  const initializeDefaultValue = useCallback(
    (sharedList: SharedList) => {
      const { data } = sharedList;
      if (defaultValue && value === undefined) {
        const defaultChoice = parseChoice(defaultValue);

        if (!defaultChoice) return;

        const choice = data.find(
          ({ label, value: choiceValue }: Choice) =>
            StringExtension.isTheSame(defaultChoice.label, label) ||
            StringExtension.isTheSame(defaultChoice.value, choiceValue),
        );

        if (choice) field.value = choice;
      }
    },
    [defaultValue, value, field],
  );

  // Récupération des choices
  const lookedForList = useRef(false);
  useEffect(() => {
    if (listChoice && listChoice.length > 0) {
      setChoices(toJS(listChoice));
      lookedForList.current = true;
      return;
    }

    // Listes applicatives (##documents, ##declaration) : remplies à l'hydratation, jamais présentes dans les listes partagées
    if (isApplicativeListId(listId)) {
      setChoices(toJS(listChoice ?? []));
      return;
    }

    if (listId && lookedForList.current === false) {
      getSharedList(listId).then((sharedList) => {
        if (sharedList) {
          setChoices(toJS(listChoice ?? sharedList.data));
          updateProperty(fullPathId, 'listChoice', listChoice ?? sharedList.data);
          // Initialisation des valeurs par défaut:
          initializeDefaultValue(sharedList);
        } else {
          // Liste introuvable : aucun choix
          setChoices([]);
        }
      });

      lookedForList.current = true;
    }
  }, [getSharedList, listId, initializeDefaultValue, defaultValue, fullPathId, updateProperty, listChoice]);

  useEffect(() => {
    if (listChoice && filterDef) {
      setChoices(listChoice);
    }
  }, [filterDef, listChoice]);

  const handleDeleteAll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      updateDeclaration(fullPathId, '');
      setIsDeleteAll(true);
      setSelectedIndex(undefined);
    },
    [fullPathId, setSelectedIndex, updateDeclaration],
  );

  // Calcul du choix sélectionné (selectedChoice)
  let selectedChoice: Choice | undefined = undefined;
  if (choices && field.value) {
    selectedChoice = choices.find((choice) => choice.value === (field.value as Choice).value);
  }

  // On retourne les valeurs à la vue
  return {
    t,
    choices,
    nameWithId,
    toolTip,
    id,
    fullPathId,
    handleDeleteAll,
    isDeleteAll,
    setIsDeleteAll,
    selectedChoice,
  };
};

export default useData;
